import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  TextField,
  Typography,
  Tooltip,
  CircularProgress,
  Select,
  MenuItem,
  Divider
} from '@material-ui/core';
import { Edit as EditIcon, Delete as DeleteIcon, Save as SaveIcon, Cancel as CancelIcon } from '@material-ui/icons';
import axios from 'axios';
import { theme } from 'apps/haulpay-frontend/src/theme';

const useStyles ={
  dialogPaper: {
    minWidth: '700px',
    maxWidth: '900px'
  },
  addButton: {
    marginBottom: '2px'
  },
  tableContainer: {
    maxHeight: '400px',
    overflow: 'auto'
  },
  emptyState: {
    textAlign: 'center',
    padding: '4px',
    color: theme.palette.text.secondary
  },
  editRow: {
    backgroundColor: theme.palette.primary.light,
  },
  rowCell: {
    width: '240px'
  },
  actionButtons: {
    paddingRight: '5px'
  }
};

const DEFAULT_SERVICE_OPTIONS = ['haulpay api', 'turvo'];
const JSON_FIELD_SERVICE = 'turvo';

const ServiceNameSelect = ({ value, onChange, disabled, options }) => (
  <Select
    fullWidth
    size="small"
    value={value}
    onChange={onChange}
    disabled={disabled}
  >
    {options.map((opt) => (
      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
    ))}
  </Select>
);

const ExternalCodesModal = ({
  open,
  onClose,
  title,
  endpoints,
  metaType,
  factoringUserId,
  referenceUserId,
  referenceDebtorId,
  serviceOptions = DEFAULT_SERVICE_OPTIONS,
  onUpdate,
  onReassign,
}) => {
  const [externalCodes, setExternalCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingModel, setEditingModel] = useState(null);
  const [recordId, setRecordId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState({ service_name: '', code: '' });
  const [error, setError] = useState('');
  const [lookupCode, setLookupCode] = useState('');
  const [lookupResults, setLookupResults] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const latestRequestId = useRef(0);
  const latestLookupRequestId = useRef(0);

  useEffect(() => {
    if (open && endpoints?.merged) {
      setExternalCodes([]);
      fetchExternalCodes();
    }
    if (!open) {
      resetForm();
      setError('');
      resetLookup();
    }
  }, [open, endpoints?.merged]);

  const fetchExternalCodes = async () => {
    const requestId = ++latestRequestId.current;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(endpoints.merged);
      const codes = response.data || [];
      if (requestId === latestRequestId.current) {
        setExternalCodes(codes);
      }
      return codes;
    } catch (err) {
      if (requestId === latestRequestId.current) {
        setError('Failed to load external codes');
      }
      return [];
    } finally {
      if (requestId === latestRequestId.current) {
        setLoading(false);
      }
    }
  };

  const handleAdd = () => {
    setIsAdding(true);
    setFormData({ service_name: serviceOptions[0], code: '' });
    setEditingRowId(null);
  };

  const handleEdit = (code) => {
    setEditingRowId(code.row_id);
    setEditingModel(code.model);
    setRecordId(code.id);
    setFormData({ service_name: code.service_name, code: code.code });
    setIsAdding(false);
  };

  const flattenErrorData = (data) => {
    if (data == null) return null;
    if (typeof data === 'string') return data;
    if (Array.isArray(data)) {
      for (const item of data) {
        const msg = flattenErrorData(item);
        if (msg) return msg;
      }
      return null;
    }
    if (typeof data === 'object') {
      for (const key of ['detail', 'code', 'external_code', 'service_name', 'non_field_errors']) {
        if (key in data) {
          const msg = flattenErrorData(data[key]);
          if (msg) return msg;
        }
      }
      for (const value of Object.values(data)) {
        const msg = flattenErrorData(value);
        if (msg) return msg;
      }
    }
    return null;
  };

  const extractErrorMessage = (err) => (
    flattenErrorData(err?.response?.data)
    || flattenErrorData(err)
    || 'Failed to save external code. Please try again.'
  );

  const handleSave = async () => {
    if (!formData.service_name || !formData.code) {
      setError('Both Source and External ID are required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      if (isAdding) {
        if (formData.service_name.toLowerCase() === JSON_FIELD_SERVICE) {
          await axios.post(endpoints.merged, {
            service_name: formData.service_name,
            code: formData.code,
          });
        } else {
          const body = {
            service_name: formData.service_name,
            code: formData.code,
            client: factoringUserId,
            meta_type: metaType,
          };
          if (referenceUserId) {
            body.user = referenceUserId;
          }
          await axios.post(endpoints.referenceCode, body);
        }
      } else if (editingRowId) {
        if (editingModel === 'ExternalReferenceCode') {
          await axios.patch(`${endpoints.referenceCode}${recordId}/`, {
            service_name: formData.service_name,
            code: formData.code,
          });
        } else {
          await axios.patch(endpoints.merged, {
            service_name: formData.service_name,
            code: formData.code,
          });
        }
      }
      const updatedCodes = await fetchExternalCodes();
      resetForm();
      if (onUpdate) onUpdate(updatedCodes.length);
    } catch (err) {
      setError(extractErrorMessage(err));
      setFormData((prev) => ({ ...prev, code: '' }));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm('Are you sure you want to delete this external code?')) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      if (code.model === 'ExternalReferenceCode') {
        await axios.delete(`${endpoints.referenceCode}${code.id}/`);
      } else {
        await axios.delete(endpoints.merged, {
          data: { service_name: code.service_name },
        });
      }
      const updatedCodes = await fetchExternalCodes();
      if (onUpdate) onUpdate(updatedCodes.length);
    } catch (err) {
      setError('Failed to delete external code');
    } finally {
      setLoading(false);
    }
  };

  const resetLookup = () => {
    latestLookupRequestId.current += 1;
    setLookupCode('');
    setLookupResults(null);
    setLookupLoading(false);
  };

  const handleLookup = async () => {
    const code = lookupCode.trim();
    if (!code) return;
    const requestId = ++latestLookupRequestId.current;
    setLookupLoading(true);
    setError('');
    try {
      const response = await axios.get(endpoints.lookup, {
        params: { code, client: factoringUserId },
      });
      if (requestId === latestLookupRequestId.current) {
        setLookupResults(response.data || []);
      }
    } catch (err) {
      if (requestId === latestLookupRequestId.current) {
        setError(extractErrorMessage(err));
        setLookupResults([]);
      }
    } finally {
      if (requestId === latestLookupRequestId.current) {
        setLookupLoading(false);
      }
    }
  };

  const handleReassign = async (referenceCodeId) => {
    const body = referenceUserId
      ? { user: referenceUserId }
      : { debtor: referenceDebtorId };
    setLookupLoading(true);
    setError('');
    try {
      await axios.post(`${endpoints.reassign}${referenceCodeId}/reassign/`, body);
      const updatedCodes = await fetchExternalCodes();
      if (onUpdate) onUpdate(updatedCodes.length);
      // A move also changes the record the code came from, so refresh the whole list.
      if (onReassign) onReassign();
      await handleLookup();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLookupLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setError('');
  };

  const resetForm = () => {
    setIsAdding(false);
    setEditingRowId(null);
    setEditingModel(null);
    setRecordId(null);
    setFormData({ service_name: '', code: '' });
    setError('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {title}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" variant="body2" gutterBottom>
            {error}
          </Typography>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleAdd}
          disabled={isAdding || editingRowId !== null || loading}
          style={useStyles.addButton}
        >
          Add External Code
        </Button>

        <div style={useStyles.tableContainer}>
          {loading && externalCodes.length === 0 ? (
            <div
              style={{ display: 'flex', justifyContent: 'center', padding: 16 }}
            >
              <CircularProgress />
            </div>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={useStyles.rowCell}>
                    <strong>Source</strong>
                  </TableCell>
                  <TableCell>
                    <strong>External ID</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isAdding && (
                  <TableRow style={useStyles.editRow}>
                    <TableCell>
                      <ServiceNameSelect
                        value={formData.service_name}
                        onChange={(e) =>
                          handleChange('service_name', e.target.value)
                        }
                        disabled={loading}
                        options={serviceOptions}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="External ID"
                        value={formData.code}
                        onChange={(e) => handleChange('code', e.target.value)}
                        disabled={loading}
                      />
                    </TableCell>
                    <TableCell align="right" style={useStyles.actionButtons}>
                      <div>
                        <Tooltip title="Save">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={handleSave}
                            disabled={loading}
                          >
                            <SaveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton
                            size="small"
                            onClick={resetForm}
                            disabled={loading}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {externalCodes.map((code) => (
                  <TableRow
                    key={code.row_id}
                    style={
                      editingRowId === code.row_id ? { ...useStyles.editRow } : {}
                    }
                  >
                    <TableCell>
                      {code.service_name}
                    </TableCell>
                    <TableCell>
                      {editingRowId === code.row_id ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={formData.code}
                          onChange={(e) => handleChange('code', e.target.value)}
                          disabled={loading}
                        />
                      ) : (
                        code.code
                      )}
                    </TableCell>
                    <TableCell align="right" style={useStyles.actionButtons}>
                      {editingRowId === code.row_id ? (
                        <div>
                          <Tooltip title="Save">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={handleSave}
                              disabled={loading}
                            >
                              <SaveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <IconButton
                              size="small"
                              onClick={resetForm}
                              disabled={loading}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </div>
                      ) : (
                        <div>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(code)}
                              disabled={
                                loading || isAdding || editingRowId !== null
                              }
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleDelete(code)}
                              disabled={
                                loading || isAdding || editingRowId !== null
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!isAdding && externalCodes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <div style={useStyles.emptyState}>
                        <Typography variant="body2">
                          No external codes found. Click "Add External Code" to
                          create one.
                        </Typography>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {endpoints?.lookup && (
          <div style={{ marginTop: 16 }}>
            <Divider />
            <Typography variant="subtitle2" style={{ margin: '12px 0 8px' }}>
              Find who holds a code (this client)
            </Typography>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="External ID"
                value={lookupCode}
                onChange={(e) => setLookupCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleLookup();
                }}
                disabled={lookupLoading}
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={handleLookup}
                disabled={lookupLoading || !lookupCode.trim()}
              >
                Search
              </Button>
              {lookupLoading && <CircularProgress size={20} />}
            </div>

            {lookupResults !== null && (
              lookupResults.length === 0 ? (
                <Typography variant="body2" style={{ ...useStyles.emptyState }}>
                  No records hold that code for this client.
                </Typography>
              ) : (
                <Table size="small" style={{ marginTop: 8 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Holder</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>External ID</strong></TableCell>
                      <TableCell align="right"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lookupResults.map((result) => (
                      <TableRow key={result.id}>
                        <TableCell>{result.holder_name || result.holder_user_id || '—'}</TableCell>
                        <TableCell>{result.meta_type}</TableCell>
                        <TableCell>{result.code}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="primary"
                            onClick={() => handleReassign(result.id)}
                            disabled={lookupLoading}
                          >
                            {metaType === 'payment_profile' ? 'Move to this carrier' : 'Move to this debtor'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            resetForm();
            onClose();
          }}
          color="primary"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExternalCodesModal;
